import React from 'react';
import { ResponsiveTooltip, Typography } from '../../../../components';
import { Tooltip } from '@heroui/react';
import {
  DoubleArrowsClient,
  InfoCircle,
  BronzeLvl,
  SilverLvl,
  GoldLvl,
  PlatinumLvl,
  DiamondLvl,
} from '../../../../components/icons';

interface PartnerLevelProgressProps {
  currentMonth: string;
  currentLevel: string;
  currentLevelKey: string;
  commission: string;
  nextMonth: string;
  currentAmount: number;
  targetAmount: number;
  nextLevel: string;
  progress: number; // 0-100
  t: (key: string, params?: Record<string, any>) => string;
}

const PartnerLevelProgress: React.FC<PartnerLevelProgressProps> = ({
  currentMonth,
  currentLevel,
  currentLevelKey,
  commission,
  nextMonth,
  currentAmount,
  targetAmount,
  nextLevel,
  progress,
  t,
}) => {
  const getLevelIcon = (levelKey: string) => {
    const iconMap: Record<string, React.ReactNode> = {
      bronze: <BronzeLvl />,
      silver: <SilverLvl />,
      gold: <GoldLvl />,
      platinum: <PlatinumLvl />,
      diamond: <DiamondLvl />,
    };
    return iconMap[levelKey] || <BronzeLvl />;
  };

  return (
    <div className="flex flex-col gap-1 md:min-w-[17.5rem] w-full md:w-[25rem] bg-white rounded-3">
      {/* Header */}
      <div className="flex items-center justify-between w-full">
        <div className="flex md:flex-row flex-col gap-1 md:gap-2 md:items-center">
          <div className="flex gap-1 items-center">
            <Typography variant="body2" className="text-auth-muted">
              {currentMonth}
            </Typography>
            <div className="flex gap-0.5 items-center">
              {getLevelIcon(currentLevelKey)}
              <Typography variant="label2" className="!capitalize">
                {currentLevel}
              </Typography>
            </div>
          </div>
          <div className="flex gap-1 items-center">
            <Typography variant="body2" className="text-auth-muted">
              {t('proposals.partnerLevelProgress.commission')}
            </Typography>
            <Typography variant="label2" className="">
              {commission}
            </Typography>
          </div>
        </div>
        <ResponsiveTooltip
          content={t('proposals.partnerLevelProgress.workingConditions')}
          placement="top"
        >
          <div className="flex gap-0.5 items-center cursor-pointer">
            <Typography variant="body3" className="">
              {t('proposals.partnerLevelProgress.workingConditions')}
            </Typography>
            <InfoCircle />
          </div>
        </ResponsiveTooltip>
      </div>

      {/* Progress Bar */}
      <div className="flex flex-col gap-2 w-full">
        <div className="bg-tin rounded-2 h-3 w-full overflow-hidden">
          <div
            className="bg-primary h-full rounded-2 transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Details */}
        <div className="flex items-center justify-between w-full">
          <div className="flex gap-1 items-center">
            <Typography variant="body3" className="text-auth-muted">
              {nextMonth}
            </Typography>
            <Typography variant="label3">
              <span className="">${currentAmount.toLocaleString()}</span>
              <span className="text-auth-muted"> / ${targetAmount.toLocaleString()}</span>
            </Typography>
          </div>
          <div className="flex gap-1 items-center">
            <Typography variant="body3" className="text-auth-muted">
              {currentLevel}
            </Typography>
            <DoubleArrowsClient />
            <Typography variant="label3" className="">
              {nextLevel}
            </Typography>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartnerLevelProgress;
